// Deja solo el escrito de una respuesta de Chatbularis, para el Word o el PDF.
//
// Vive en su propio módulo, sin dependencias, para poder probarlo con Jest:
// exportarEscrito.ts importa `marked` y `docx`, que Jest de CRA no transforma.

/** Líneas que son del chat y no del escrito. Aunque el prompt le pide al
 *  modelo que en una entrega en Word o PDF responda SOLO con el documento, un
 *  archivo que se presenta en un tribunal no puede depender de que siempre
 *  obedezca: una frase como "también te puedo entregar el modelo así" adentro
 *  de una demanda es un error grave que nadie ve hasta que ya se presentó. */
const LINEA_DE_CHAT = [
  // Las dos frases que el propio prompt le pedía al modelo, tal cual las
  // escribe; nada más. Una regla amplia ("cualquier línea que diga
  // asistente") borraba frases legítimas en medio del escrito.
  /borrador para revisi[oó]n profesional/i,
  /puedes descargar este escrito/i,
  /\bchatbularis\b/i,
];

/** Con qué empieza un párrafo que le habla al abogado y no al tribunal. Solo
 *  se quitan al principio y al final de la respuesta, que es donde el modelo
 *  saluda y se despide; en medio del escrito no se toca nada. Y solo giros
 *  que un escrito o una carta NO usan: "De acuerdo con lo ordenado…" o "A
 *  continuación se individualizan…" son inicios normales y quedan. */
const APERTURA_DE_CHAT = /^(aqu[ií] (tienes|est[aá]|va|te (dejo|entrego|presento))|te (dejo|entrego|presento|preparo|prepar[eé]|comparto)|claro|perfecto|por supuesto|con (mucho )?gusto|como (me )?(pediste|solicitaste)|seg[uú]n (lo )?solicitado)\b/i;
/** Despedidas de chat. Los cierres de una carta al cliente ("Quedo atento a
 *  sus comentarios", "Importante: la audiencia es el 5 de octubre", "Nota:
 *  adjunte su cédula") son parte de la carta y NO se tocan: solo se quita lo
 *  que ofrece seguir conversando con quien escribe. */
const CIERRE_DE_CHAT = /^(si (necesitas|quieres|deseas|requieres|prefieres) (que|alg[uú]n|alguna|otro|otra|m[aá]s|ajust|cambi|modific|agreg|incorpor)|¿(quieres|deseas|necesitas|te gustar[ií]a) que|tambi[eé]n (puedo|te puedo|podr[ií]a)|puedo (ajustar|adaptar|entregar|preparar|redactar|agregar|desarrollar)|podr[ií]a (ajustar|adaptar|entregar|preparar|desarrollar)|espero que (te|le) (sirva|sea [uú]til)|no dudes en|av[ií]same si)/i;

/** Deja solo el escrito: quita las líneas de chat y los párrafos de saludo
 *  al principio y de despedida al final. Los párrafos se conservan tal cual
 *  (con su sangría: una lista anidada la necesita); solo se miran recortados
 *  para decidir. Exportado para probarlo. */
export function soloElEscrito(markdown: string): string {
  const lineas = markdown.replace(/\r\n/g, '\n').split('\n').filter((l) => !LINEA_DE_CHAT.some((re) => re.test(l)));
  const parrafos = lineas.join('\n').split(/\n{2,}/).filter((p) => p.trim());
  const cabeza = (p: string) => p.trim().replace(/^[*_>\s]+/, '');
  const esTitulo = (p: string) => /^#{1,6}\s/.test(p.trim()) || /^\*\*[^*]+\*\*$/.test(p.trim());
  // Al principio: saludos hasta el primer párrafo que parezca del escrito.
  while (parrafos.length > 1 && !esTitulo(parrafos[0]) && APERTURA_DE_CHAT.test(cabeza(parrafos[0]))) parrafos.shift();
  // Al final: despedidas y ofrecimientos. Hasta tres párrafos, que es lo que
  // suele haber; más que eso ya sería contenido.
  let quitados = 0;
  while (parrafos.length > 1 && quitados < 3 && CIERRE_DE_CHAT.test(cabeza(parrafos[parrafos.length - 1]))) { parrafos.pop(); quitados++; }
  // Una raya final que quedó separando el escrito de la despedida ya no separa nada.
  while (parrafos.length > 1 && /^(-{3,}|\*{3,}|_{3,})$/.test(parrafos[parrafos.length - 1].trim())) parrafos.pop();
  return parrafos.map((p) => p.replace(/^\n+|\n+$/g, '')).join('\n\n').trim();
}
